import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import {
  forecastRequestStatus,
  type default as ForecastRequestStatus,
} from './ForecastRequestStatus.js'
import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import { organizationId, type OrganizationId } from './Organization.js'
import { projectId, type ProjectId } from './Project.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.forecast_request_ */
export type ForecastRequestId = string & { __brand: 'ForecastRequestId' }

/** Represents the table public.forecast_request_ */
export default interface ForecastRequestTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    ForecastRequestId,
    ForecastRequestId | undefined,
    ForecastRequestId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.uuid */
  userId: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.int4 */
  projectId: ColumnType<ProjectId | null, ProjectId | null, ProjectId | null>

  /** Database type: pg_catalog.int4 */
  organizationId: ColumnType<
    OrganizationId | null,
    OrganizationId | null,
    OrganizationId | null
  >

  /** Database type: public.forecast_request_status_ */
  status: ColumnType<
    ForecastRequestStatus,
    ForecastRequestStatus | undefined,
    ForecastRequestStatus
  >

  /** Database type: pg_catalog.timestamptz */
  requestedAt: ColumnType<Date, Date | string | undefined, Date | string>

  /**
   * Database type: pg_catalog.text
   * The model chosen AT THIS approval -- an immutable audit record, not current truth (#4169 D3). Current truth is legacy_site_.forecast_model_type.
   */
  modelType: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.uuid */
  approvedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  approvedAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >
}

export type ForecastRequest = Selectable<ForecastRequestTable>

export type ForecastRequestInitializer = Insertable<ForecastRequestTable>

export type ForecastRequestMutator = Updateable<ForecastRequestTable>

export const forecastRequestId =
  z.string() as unknown as z.Schema<ForecastRequestId>

export const forecastRequest = z.object({
  id: forecastRequestId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  userId: userId.nullable().optional(),
  siteId: legacySiteId,
  projectId: projectId.nullable().optional(),
  organizationId: organizationId.nullable().optional(),
  status: forecastRequestStatus,
  requestedAt: z.coerce.date(),
  modelType: z.string().nullable().optional(),
  approvedBy: userId.nullable().optional(),
  approvedAt: z.coerce.date().nullable().optional(),
}) as unknown as z.Schema<ForecastRequest>

export const forecastRequestInitializer = z.object({
  id: forecastRequestId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  userId: userId.optional().nullable(),
  siteId: legacySiteId,
  projectId: projectId.optional().nullable(),
  organizationId: organizationId.optional().nullable(),
  status: forecastRequestStatus.optional(),
  requestedAt: z.coerce.date().optional(),
  modelType: z.string().optional().nullable(),
  approvedBy: userId.optional().nullable(),
  approvedAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<ForecastRequestInitializer>

export const forecastRequestMutator = z.object({
  id: forecastRequestId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: z.string().optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: z.string().optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: z.string().optional().nullable(),
  userId: userId.optional().nullable(),
  siteId: legacySiteId.optional(),
  projectId: projectId.optional().nullable(),
  organizationId: organizationId.optional().nullable(),
  status: forecastRequestStatus.optional(),
  requestedAt: z.coerce.date().optional(),
  modelType: z.string().optional().nullable(),
  approvedBy: userId.optional().nullable(),
  approvedAt: z.coerce.date().optional().nullable(),
}) as unknown as z.Schema<ForecastRequestMutator>
